import apiClient from './apiClient';

// Types based on the example response format
export interface SubscriptionPlan {
  _id: string;
  name: string;
  description: string;
  features: string[];
  interviewMinutes: number;
  price: number;
  discountPercentage: number;
  planType: 'free' | 'monthly' | 'yearly' | 'quarterly';
  isAddon: boolean;
  currentPrice: number;
  createdAt: string;
  updatedAt: string;
  isRecurring?: boolean;
  isPurchasable: boolean;
  isUnlimited: boolean;
  }

// Mock data for fallback when API is unavailable
const FALLBACK_PLANS: SubscriptionPlan[] = [
  {
    _id: 'monthly-plan',
    name: 'Monthly Pro',
    description: 'Full access for a month.',
    features: [
      'Unlimited Interviews',
      'Real-time AI Feedback',
      'Performance Analytics',
      'Interview Recordings'
    ],
    interviewMinutes: 120,
    price: 499,
    discountPercentage: 15,
    planType: 'monthly',
    isAddon: false,
    currentPrice: 349,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    isRecurring: true,
    isPurchasable: true,
    isUnlimited: false
  },
  {
    _id: 'yearly-plan',
    name: 'Yearly Pro',
    description: 'Full access for a year at a discounted rate.',
    features: [
      'All Monthly Features',
      'Priority Support',
      'Advanced Analytics',
      'Technical & Behavioral Assessment'
    ],
    interviewMinutes: 400,
    price: 1800,
    discountPercentage: 25,
    planType: 'yearly',
    isAddon: false,
    currentPrice: 1500,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    isRecurring: true,
    isPurchasable: true,
    isUnlimited: false
  },
  {
    _id: 'basic-addon',
    name: 'Basic Bundle',
    description: 'Additional interview practice.',
    features: [
      'Basic Performance Metrics',
      'Valid for 30 days'
    ],
    interviewMinutes: 60,
    price: 899,
    discountPercentage: 0,
    planType: 'monthly',
    isAddon: true,
    currentPrice: 899,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    isRecurring: false,
    isPurchasable: true,
    isUnlimited: false
  },
  {
    _id: 'pro-addon',
    name: 'Pro Bundle',
    description: 'Comprehensive interview practice.',
    features: [
      'Detailed Performance Analysis',
      'Valid for 45 days'
    ],
    interviewMinutes: 140,
    price: 1499,
    discountPercentage: 10,
    planType: 'monthly',
    isAddon: true,
    currentPrice: 1349,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    isRecurring: false,
    isPurchasable: true,
    isUnlimited: false
  }
];

class SubscriptionService {
  private plans: SubscriptionPlan[] = [];
  private lastFetchTime: number = 0;
  private cacheDuration: number = 5 * 60 * 1000; // 5 minutes in milliseconds
  private fetchPromise: Promise<SubscriptionPlan[]> | null = null;
  
  /**
   * Get subscription plans from API
   * 
   * @param forceRefresh Force a refresh of plans from API
   * @returns Array of subscription plans
   */
  public async getSubscriptionPlans(forceRefresh: boolean = false): Promise<SubscriptionPlan[]> {
    const currentTime = Date.now();
    
    // Return cached data if available and not expired
    if (
      !forceRefresh && 
      this.plans.length > 0 && 
      currentTime - this.lastFetchTime < this.cacheDuration
    ) {
      console.log('SubscriptionService: Using cached plans');
      return this.plans;
    }
    
    // If we already have a fetch in progress, return that promise to avoid multiple API calls
    if (this.fetchPromise) {
      console.log('SubscriptionService: Using existing fetch promise');
      return this.fetchPromise;
    }
    
    // Create a new fetch promise
    this.fetchPromise = (async () => {
      try {
        console.log('SubscriptionService: Fetching plans from API');
        const response = await apiClient.get<any>('/subscriptions/plans');
        
        console.log('SubscriptionService: Raw API response:', response);
        
        // Check for _response property which indicates it went through our normalizeApiResponse util
        if (response && response._response) {
          // Convert the response object to an array
          const plansArray: SubscriptionPlan[] = [];
          
          // Iterate through numeric keys to collect plans
          Object.keys(response).forEach(key => {
            // Skip the _response key and only process numeric indices
            if (key !== '_response' && !isNaN(Number(key))) {
              const plan = response[key] as SubscriptionPlan;
              console.log(`SubscriptionService: Processing plan ${key}:`, plan.name);
              plansArray.push(plan);
            }
          });
          
          console.log(`SubscriptionService: Extracted ${plansArray.length} plans from response`);
          
          if (plansArray.length > 0) {
            this.plans = plansArray;
            this.lastFetchTime = currentTime;
            console.log('SubscriptionService: Plans fetched successfully', this.plans);
            return this.plans;
          }
        }
        
        console.error('SubscriptionService: Unexpected response format', response);
        // If API fails, use fallback data in development environment
        if (import.meta.env.DEV) {
          console.warn('SubscriptionService: Using fallback data for development');
          this.plans = FALLBACK_PLANS;
          this.lastFetchTime = currentTime;
          return this.plans;
        }
        throw new Error('Invalid response format');
      } catch (error) {
        console.error('SubscriptionService: Error fetching plans', error);
        // If API is unavailable, use fallback data in development environment
        if (import.meta.env.DEV) {
          console.warn('SubscriptionService: Using fallback data for development due to API error');
          this.plans = FALLBACK_PLANS;
          this.lastFetchTime = currentTime;
          return this.plans;
        }
        throw error;
      } finally {
        // Clear the fetch promise after a delay to prevent immediate duplicate requests
        setTimeout(() => {
          this.fetchPromise = null;
        }, 1000);
      }
    })();
    
    return this.fetchPromise;
  }
  
  /**
   * Get subscription plans filtered by type (regular plans or add-ons)
   * 
   * @param forceRefresh Force a refresh of plans from API
   * @returns Filtered array of subscription plans
   */
  public async getSubscriptionPlansByType(
    forceRefresh: boolean = false
  ): Promise<SubscriptionPlan[]> {
    try {
      const allPlans = await this.getSubscriptionPlans(forceRefresh);
      return allPlans;
    } catch (error) {
      console.error(`SubscriptionService: Error fetching plans`, error);
      throw error;
    }
  }
  
  /**
   * Calculate the approximate number of interviews based on minutes
   * 
   * @param minutes Total interview minutes
   * @returns String representing the range of possible interviews
   */
  public calculateInterviews(minutes: number, isUnlimited: boolean): string {
    if (isUnlimited) {
      return "∞";
    }
    // Assuming an average interview is about 20-25 minutes
    const minInterviews = Math.floor(minutes / 25);
    const maxInterviews = Math.ceil(minutes / 20);
    
    return minInterviews === maxInterviews 
      ? `${minInterviews}` 
      : `${minInterviews}-${maxInterviews}`;
  }
}

const subscriptionService = new SubscriptionService();
export default subscriptionService; 